import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { showNewTrendToast, trendToastTitle } from "@/lib/newTrendToast";
import { deliverTrendAlert } from "@/lib/trendAlertDelivery";

import {
  planTrendToast,
  readTrendNotifySettings,
  writeTrendNotifySettings,
} from "@/lib/trendNotifications";


export type ApprovedTrend = {
  id: string;
  title: string;
  summary: string;
  niche: string | null;
  tags: string[];
  total_score: number;
  velocity_score: number;
  volume_score: number;
  relevance_score: number;
  source_slugs: string[];
  blueprint: any;
  published_at: string | null;
};

export const APPROVED_TRENDS_SELECT =
  "id, title, summary, niche, tags, total_score, velocity_score, volume_score, relevance_score, source_slugs, blueprint, published_at";

/**
 * Single source of truth for the curated (approved) trends shown on the
 * dashboard. Both the curated feed and the dashboard stat cards read from this
 * query so the numbers always match the rows actually displayed.
 */
export async function fetchApprovedTrends(
  client: { from: (t: string) => any } = supabase,
): Promise<ApprovedTrend[]> {
  const { data, error } = await client
    .from("trends")
    .select(APPROVED_TRENDS_SELECT)
    .eq("status", "approved")
    .order("published_at", { ascending: false })
    .limit(60);
  if (error) {
    throw new Error(error.message || "Could not load curated trends.");
  }
  return (data as ApprovedTrend[]) ?? [];
}

export type TrendStats = {
  /** Total approved trends available to this account. */
  total: number;
  /** Approved trends published within the current UTC day. */
  newToday: number;
  /** Approved trends that actually carry a blueprint payload. */
  blueprintsReady: number;
};

const hasBlueprint = (b: unknown): boolean => {
  if (!b) return false;
  if (Array.isArray(b)) return b.length > 0;
  if (typeof b === "object") return Object.keys(b as object).length > 0;
  return true;
};

const isToday = (iso: string | null, now: Date): boolean => {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return d.toISOString().slice(0, 10) === now.toISOString().slice(0, 10);
};

/** Derives the dashboard stat-card numbers from the exact feed rows. */
export function deriveTrendStats(
  trends: ApprovedTrend[],
  now: Date = new Date(),
): TrendStats {
  return {
    total: trends.length,
    newToday: trends.filter((t) => isToday(t.published_at, now)).length,
    blueprintsReady: trends.filter((t) => hasBlueprint(t.blueprint)).length,
  };
}

/* ------------------------------------------------------------------ *
 * Cached fallback
 * ------------------------------------------------------------------ */

export const APPROVED_TRENDS_CACHE_KEY = "td:approved-trends:v1";
/** Cached rows older than this are considered too stale to display. */
export const APPROVED_TRENDS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export type ApprovedTrendsCache = {
  rows: ApprovedTrend[];
  savedAt: number;
};

export function writeApprovedTrendsCache(
  rows: ApprovedTrend[],
  now: number = Date.now(),
): void {
  try {
    window.localStorage.setItem(
      APPROVED_TRENDS_CACHE_KEY,
      JSON.stringify({ rows, savedAt: now } satisfies ApprovedTrendsCache),
    );
  } catch {
    /* storage unavailable (private mode / quota) — cache is best-effort */
  }
}

export function readApprovedTrendsCache(
  now: number = Date.now(),
): ApprovedTrendsCache | null {
  try {
    const raw = window.localStorage.getItem(APPROVED_TRENDS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ApprovedTrendsCache;
    if (!parsed || !Array.isArray(parsed.rows)) return null;
    if (typeof parsed.savedAt !== "number") return null;
    if (now - parsed.savedAt > APPROVED_TRENDS_CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearApprovedTrendsCache(): void {
  try {
    window.localStorage.removeItem(APPROVED_TRENDS_CACHE_KEY);
  } catch {
    /* noop */
  }
}

/* ------------------------------------------------------------------ *
 * Shared realtime channel (one per app, refcounted)
 * ------------------------------------------------------------------ */

type Listener = () => void;

const listeners = new Set<Listener>();
let sharedChannel: any = null;
let subscribeCount = 0;

/** Test hook: observable state of the shared realtime channel. */
export function __approvedTrendsRealtimeDebug() {
  return {
    listeners: listeners.size,
    channelActive: sharedChannel !== null,
    /** How many times a channel was actually created + subscribed. */
    subscribeCount,
  };
}

/**
 * Subscribes to approved-trend changes through a single shared Supabase
 * channel. Repeated dashboard visits (mount/unmount cycles across route
 * changes) attach to the same channel instead of opening a new subscription
 * each time, which previously caused duplicated refetches and inflated counts.
 */
export function subscribeToApprovedTrends(listener: Listener): () => void {
  listeners.add(listener);
  if (!sharedChannel) {
    subscribeCount += 1;
    sharedChannel = supabase
      .channel(`approved-trends:${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "trends" },
        () => {
          listeners.forEach((l) => l());
        },
      )
      .subscribe();
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && sharedChannel) {
      supabase.removeChannel(sharedChannel);
      sharedChannel = null;
    }
  };
}

/** Ids present in `next` but not in `prev`. */
export function newTrendIds(
  prev: ApprovedTrend[] | null,
  next: ApprovedTrend[],
): string[] {
  if (!prev) return [];
  const seen = new Set(prev.map((t) => t.id));
  return next.filter((t) => !seen.has(t.id)).map((t) => t.id);
}

export function useApprovedTrends() {
  const initialCache = readApprovedTrendsCache();
  const [trends, setTrends] = useState<ApprovedTrend[] | null>(
    () => initialCache?.rows ?? null,
  );
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [loading, setLoading] = useState(true);
  /** Epoch ms of the data currently on screen (fresh fetch or cache write). */
  const [updatedAt, setUpdatedAt] = useState<number | null>(
    initialCache?.savedAt ?? null,
  );
  const latest = useRef<ApprovedTrend[] | null>(null);
  const announced = useRef(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchApprovedTrends();
      const added = newTrendIds(latest.current, rows);
      latest.current = rows;
      setTrends(rows);
      setError(null);
      setStale(false);
      const savedAt = Date.now();
      writeApprovedTrendsCache(rows, savedAt);
      setUpdatedAt(savedAt);
      if (announced.current) {
        const { plan, settings } = planTrendToast(added, readTrendNotifySettings());
        writeTrendNotifySettings(settings);
        showNewTrendToast(plan);
        if (plan.kind !== "none") {
          // Email/web push fan-out for users who opted into those channels.
          void deliverTrendAlert(plan, trendToastTitle(plan), readTrendNotifySettings());
        }
      }

      announced.current = true;
    } catch (e: any) {
      const message = e?.message ?? "Could not load curated trends.";
      const cached = readApprovedTrendsCache();
      if (cached && cached.rows.length > 0) {
        // Brief backend outage: keep showing the last known good data.
        latest.current = cached.rows;
        setTrends(cached.rows);
        setStale(true);
        setError(null);
        setUpdatedAt(cached.savedAt);
      } else {
        setTrends(null);
        setStale(false);
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Realtime: any change to approved trends refreshes the feed and the stat
  // cards together, so they can never drift apart.
  useEffect(() => subscribeToApprovedTrends(() => refetch()), [refetch]);

  return {
    trends,
    loading,
    error,
    /** True when the displayed rows come from cache during an outage. */
    stale,
    /** Epoch ms for the age indicator ("updated X ago"). */
    updatedAt,
    stats: deriveTrendStats(trends ?? []),
    refetch,
  };
}

